import { booleanAttribute } from "#ce/utils/props";
import { DEFAULT_DISTANCES } from "#ce/shared/filters/distance/composables/useDistanceFilter";
import { DEFAULT_LOCATION_PARAM } from "#ce/shared/filters/distance/filter-names";

export const props = Object.freeze({
  /** Disables the filter's interactive controls. */
  disabled: booleanAttribute(),
  /** Optional test hook supplied by the owning finder. */
  currentLocationTestId: {
    type: String,
    default: undefined,
  },
  modelValue: {
    type: Object,
    default: undefined,
  },
  /**
   * Latitude of the selected place, as the `lat` search parameter carries it.
   *
   * @type {Number|String}
   */
  lat: {
    type: [Number, String],
    default: undefined,
  },
  /**
   * Longitude of the selected place, as the `lng` search parameter carries it.
   *
   * @type {Number|String}
   */
  lng: {
    type: [Number, String],
    default: undefined,
  },
  /**
   * The distance options (in miles) offered by the select. Accepts an array or,
   * when set as an attribute, a JSON array string (e.g. distances="[5,10,25]").
   *
   * @type {Array|String}
   */
  distances: {
    type: [Array, String],
    default: () => DEFAULT_DISTANCES,
  },
  /**
   * Offers an "Any Distance" (no-radius) option in the distance select. Absent,
   * a concrete radius is always required (e.g. the Health Plan Directory's
   * required-location flow).
   *
   * @type {Boolean|String}
   */
  allowAny: booleanAttribute(),
  /** Restore a configured radius on clear; leave absent for URL-seeded filters. */
  preserveDistanceOnClear: booleanAttribute(),
  /**
   * The selected search radius in miles, as the `distance` search parameter carries it.
   *
   * @type {Number|String}
   */
  distance: {
    type: [Number, String],
    default: undefined,
  },
  /**
   * The `distance` value that stands for "Any Distance". Choosing that option submits it, and a
   * seeded or restored distance equal to it selects the option. Absent, the option submits no
   * `distance`.
   *
   * @type {String}
   */
  anyDistanceValue: {
    type: String,
    default: undefined,
  },
  /**
   * Which control picks the distance. "select" offers the `distances` option
   * list (plus "Any Distance"); "slider" swaps in an sp-number-slider that
   * snaps through the same `distances` list, matching the Kyruus-style
   * maximum-distance control.
   *
   * @type {String}
   */
  distanceControl: {
    type: String,
    default: "select",
    validator: (value) => ["select", "slider"].includes(value),
  },
  /**
   * The selected place's search value, as the `near` search parameter carries it.
   *
   * @type {String}
   */
  near: {
    type: String,
    default: undefined,
  },
  /**
   * The search param the chosen location is submitted under. The seed is still read from `near`.
   *
   * @type {String}
   */
  locationParam: {
    type: String,
    default: DEFAULT_LOCATION_PARAM,
  },
  /**
   * Submits the current-location wording under `locationParam` alongside `lat` and `lng`, for a
   * search that lists its location by that param. Absent, a geolocated selection submits the
   * coordinates alone.
   *
   * @type {Boolean|String}
   */
  submitCurrentLocation: booleanAttribute(),
  /**
   * Decimal places the geolocated `lat` and `lng` are rounded to. Absent, they are submitted as the
   * browser reports them.
   *
   * @type {Number|String}
   * @storybook-control number
   */
  coordinatePrecision: {
    type: [Number, String],
    default: undefined,
  },
  /**
   * Bounds the location search results are biased towards, as
   * `{ sw_lat, sw_lng, ne_lat, ne_lng }` or its JSON string.
   *
   * @type {Object|String}
   */
  searchBounds: {
    type: [Object, String],
    default: undefined,
  },
  /** Common locations supplied by an embedded finder instead of the endpoint's defaults. */
  commonPlaces: {
    type: [Array, String],
    default: undefined,
  },
  /**
   * The form element name of the component.
   */
  name: {
    type: String,
    default: "distance",
  },
  /**
   * The label of the activator. Defaults to the `label` message.
   *
   * @type {String}
   */
  label: {
    type: String,
    default: undefined,
  },
  url: {
    type: String,
    default: undefined,
  },
  /**
   * The icon to prepend to the activator.
   *
   * @type {String}
   */
  prependIcon: {
    type: String,
    default: "map-pin-filled",
  },
  /**
   * When set, the suggested-location pins render inside a circular background of
   * this colour (e.g. an inverted "violet circle, white pin" treatment). Pair it
   * with the `--sp-comp-distance-filter-list-item-pin-fill-color` token to recolour
   * the pin glyph. Leave undefined for the default bare pin.
   *
   * @type {String}
   */
  locationPinBackgroundColor: {
    type: String,
    default: undefined,
  },
  hidePointer: booleanAttribute(),
  open: booleanAttribute(),
  location: {
    type: String,
    default: undefined,
  },
  inline: booleanAttribute(),
  permanent: booleanAttribute(),
  closeOnSelect: booleanAttribute(),
  closeOnContentClick: booleanAttribute(),
  /**
   * When set, the filter hides its clear affordances so the location can never
   * be emptied. Used for legally required filters.
   */
  nonClearable: booleanAttribute(),
  /**
   * The title shown at the top of the dropdown content. Defaults to the `distance_title` message.
   *
   * @type {String}
   */
  dropdownTitle: {
    type: String,
    default: undefined,
  },
  /**
   * The placeholder shown inside the location search input. Defaults to the `distance_search`
   * message.
   *
   * @type {String}
   */
  searchPlaceholder: {
    type: String,
    default: undefined,
  },
  /**
   * The heading above the suggested locations. Defaults to the `distance_popular_locations` message.
   *
   * @type {String}
   */
  locationsGroupLabel: {
    type: String,
    default: undefined,
  },
  /**
   * The geolocation button's wording. Defaults to the `current_location` message.
   *
   * @type {String}
   */
  currentLocationLabel: {
    type: String,
    default: undefined,
  },
  /**
   * The wording a geolocated selection is shown as. Defaults to the `current_location_display`
   * message.
   *
   * @type {String}
   */
  currentLocationDisplayText: {
    type: String,
    default: undefined,
  },
  /**
   * The geolocation button's wording while the position is being read. Defaults to the
   * `current_location_retrieving` message.
   *
   * @type {String}
   */
  currentLocationRetrievingLabel: {
    type: String,
    default: undefined,
  },
  /**
   * The alert shown when the position cannot be read. Defaults to the `current_location_error`
   * message.
   *
   * @type {String}
   */
  currentLocationRetrievalErrorText: {
    type: String,
    default: undefined,
  },
});
